/* eslint-disable @typescript-eslint/no-var-requires */
import { type AnyAction, type Middleware, configureStore } from '@reduxjs/toolkit';
import { Map } from 'immutable';
import localForage from 'localforage';
import every from 'lodash/every';
import isObject from 'lodash/isObject';
import { batchedSubscribe } from 'redux-batched-subscribe';
import { combineEpics, createEpicMiddleware } from 'redux-observable';
import { type Persistor, type Transform, autoRehydrate, persistStore } from 'redux-persist';
// @ts-expect-error TS(7016) FIXME: Could not find a declaration file for module 'redu... Remove this comment to see the full error message
import createMigration from 'redux-persist-migrate';
// @ts-expect-error TS(7016) FIXME: Could not find a declaration file for module 'redu... Remove this comment to see the full error message
import immutableTransform from 'redux-persist-transform-immutable';
import { BehaviorSubject, mergeMap } from 'rxjs';

import * as Constants from '../constants';
import { PERSISTOR_KEY } from '../constants/App';
import * as DebugSettingsUtil from '../lib/configuration/DebugSettingsUtil';
import getMiddleware from '../middleware';
import { EntityFilter } from '../reducers/entities';
import { buildRootReducer } from '../reducers/Index';
import { LoginFilter } from '../reducers/Login';
import { UIFilter } from '../reducers/UI';
import rootSaga from '../sagas';
import { backgroundSafeRequestAnimationFrame } from '../tools/Globals';
import tempImmutableTransform from '../tools/ImmutableMigrationTransform';
import Logger from '../tools/Logger';
import createEncryptor from '../tools/ReduxEncryptFilter';

import manifest from './Migrations';
import { setPersistor, setStore } from './StoreAccessor';
import { createPersistConfig } from './StoreAsync';
import { type RootEpic, type RootEpicMiddleware, type State, type Store } from './types';
import { reduxDevToolsConfigs } from './utils/composeEnhancers';

const log = Logger.child({ component: 'utils/StoreUtils' });

let _store: Store | null = null;
let _persistor: Persistor | null = null;
let restoredStateFromPersist = false;

export const getStore = (): Store => {
  if (!_store) {
    throw new Error('store is not configured.');
  }
  return _store;
};

export const getPersistor = (): Persistor => {
  if (!_persistor) {
    throw new Error('persistor is not configured.');
  }
  return _persistor;
};

const setupDevtoolsReplays = (
  store: Store,
  _reduxRouterMiddleware: Middleware,
  hotModule: typeof module.hot,
): void => {
  // Enable Webpack hot module replacement for reducers
  hotModule?.accept('../reducers/Index', () => {
    const nextReducer = require('../reducers/Index');
    store.replaceReducer(nextReducer);
  });
};

const setRestoredStateFromPersist = (restoredState: Partial<State>): void => {
  // restored state always at least has the `storage` key, that's why we check > 1
  restoredStateFromPersist = isObject(restoredState) && Object.keys(restoredState).length > 1;
};

const validateStore = (): boolean => {
  if (restoredStateFromPersist && _store) {
    const isStoreValid = every(Constants.Storage.REDUCERS_TO_VALIDATE_AS_MAPS, key => {
      return Map.isMap((_store as Store)[key]);
    });
    if (!isStoreValid) {
      return false;
    }
  }
  return true;
};

interface CreatedStore {
  store: Store;
  rootSaga: $TSFixMe;
  epic$: BehaviorSubject<RootEpic>;
}

/**
 * creates and configures redux store
 *
 * @returns {Promise} Promise that resolves to the newly created & configured store
 */
export const createReduxStore = (): Promise<CreatedStore> => {
  return new Promise((resolve, reject) => {
    if (_store) {
      reject(new Error('store already configured'));
    }

    const { middleware, reduxRouterMiddleware, sagaMiddleware } = getMiddleware();
    const epicMiddleware: RootEpicMiddleware = createEpicMiddleware();

    // create redux store
    _store = configureStore({
      reducer: buildRootReducer(),
      enhancers: [
        createMigration(manifest, 'storage'),
        autoRehydrate(),
        batchedSubscribe(backgroundSafeRequestAnimationFrame),
      ],
      middleware: [...middleware, epicMiddleware],
      devTools: DebugSettingsUtil.isReduxDevtoolEnabled() && reduxDevToolsConfigs,
    }) as Store;

    // Set the store singleton for the rest of the app to access
    setStore(_store);

    const epic$ = new BehaviorSubject<RootEpic>(combineEpics());

    const rootEpic: RootEpic = (action$, state$) => {
      return epic$.pipe(mergeMap(epic => epic(action$, state$, [])));
    };

    epicMiddleware.run(rootEpic);

    // extensions
    _store.asyncReducers = {};

    _store.runSaga = sagaMiddleware.run;

    if (module.hot) {
      setupDevtoolsReplays(_store, reduxRouterMiddleware, module.hot);
    }

    // App persist configuration
    let transforms: Transform<State, string>[] = [
      EntityFilter,
      UIFilter,
      LoginFilter,
      immutableTransform({}),
      tempImmutableTransform(),
    ];

    if (DebugSettingsUtil.isStorageObscured()) {
      // TODO: add ReduxCompressFilter if performance allows
      // const compressor = createCompressor();
      const encryptor = createEncryptor({
        secretKey: PERSISTOR_KEY,
        whitelist: Constants.Storage.ENCRYPTED_REDUCERS_ALLOWLIST,
      });
      transforms = transforms.concat([encryptor]);
    }

    const appPersistConfig = createPersistConfig({
      whitelist: Constants.Storage.REDUCERS_ALLOWLIST as string[],
      transforms,
    });

    log.trace('createReduxStore() - starting app persistence');
    // restored persisted state and configure future persistence
    _persistor = persistStore(_store, appPersistConfig, (_err, restoredState?: Partial<State>) => {
      if (restoredState) {
        setRestoredStateFromPersist(restoredState);
      }
      log.trace('createReduxStore() - finishing app persistence');

      log.trace('createReduxStore() - returning app store');
      resolve({ store: getStore(), rootSaga, epic$ });
    });
    // make persistor available on window for tests
    window.persistor = _persistor;
    setPersistor(_persistor);
  });
};

export const tryToRecoverFromStoreError = (): void => {
  const persistor = getPersistor();
  const isStoreValid = validateStore();
  // Should only try to purge storage and refresh if we actually
  // rehydrated from storage
  if (persistor && location && restoredStateFromPersist && !isStoreValid) {
    persistor.purge();
    location.reload();
  }
};

const deleteAllCookies = (): void => {
  const cookies = document.cookie.split(';');
  cookies.forEach(cookie => {
    const name = cookie.split('=')[0];
    document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
  });
};

export const clearLocalData = (): Promise<void> => {
  return localForage.clear().then(() => {
    deleteAllCookies();
    sessionStorage.clear();
    localStorage.clear();
    return Promise.resolve();
  });
};

export const getReducersToPurge = (): string[] => {
  const allowlistedReducers = Constants.Storage.TOTAL_ALLOWLISTED_REDUCERS;
  return allowlistedReducers.filter(key => {
    return Constants.Storage.REDUCERS_SESSION_INDEPENDENT_ALLOWLIST.indexOf(key) === -1;
  });
};

export const dispatch = <Action extends AnyAction>(action: Action): void => {
  getStore().dispatch(action);
};
